import type { MqttTopicEntry } from "../../types/mqtt";

function formatPayload(raw: string | undefined) {
  if (!raw) return "—";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 py-1">
      <div className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
        {label}
      </div>
      <div className="text-text-primary" style={{ fontSize: "var(--font-body)" }}>
        {value}
      </div>
    </div>
  );
}

export function MqttTopicDetail({ topic }: { topic: MqttTopicEntry | null }) {
  if (!topic) {
    return (
      <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
        Select a topic from the catalog.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-mono font-semibold text-text-primary" style={{ fontSize: "var(--font-heading)" }}>
          {topic.pattern}
        </h3>
        {topic.description && (
          <p className="mt-1 text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            {topic.description}
          </p>
        )}
      </div>

      <div className="rounded-md border border-border-subtle bg-page px-3 py-2">
        <MetaRow label="Publisher" value={topic.publisher ?? "—"} />
        <MetaRow label="Subscribers" value={topic.subscribers?.join(", ") ?? "—"} />
        <MetaRow label="Retained" value={topic.retained ? "Yes" : "No"} />
        <MetaRow label="QoS" value={topic.qos != null ? String(topic.qos) : "—"} />
        <MetaRow label="MQTT" value={topic.mqtt_version != null ? `v${topic.mqtt_version}` : "—"} />
        {topic.source_doc && <MetaRow label="Source" value={topic.source_doc} />}
      </div>

      {topic.user_properties && topic.user_properties.length > 0 && (
        <div>
          <div
            className="mb-1 font-semibold text-text-secondary"
            style={{ fontSize: "var(--font-micro)" }}
          >
            User properties
          </div>
          <div className="overflow-x-auto rounded-md border border-border-subtle">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-border-subtle bg-page">
                  <th className="px-2 py-1.5" style={{ fontSize: "var(--font-micro)" }}>
                    Name
                  </th>
                  <th className="px-2 py-1.5" style={{ fontSize: "var(--font-micro)" }}>
                    Example
                  </th>
                </tr>
              </thead>
              <tbody>
                {topic.user_properties.map((p) => (
                  <tr key={p.name} className="border-b border-border-subtle last:border-0">
                    <td className="px-2 py-1.5 font-mono" style={{ fontSize: "var(--font-micro)" }}>
                      {p.name}
                    </td>
                    <td className="px-2 py-1.5" style={{ fontSize: "var(--font-micro)" }}>
                      {p.example ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div
          className="mb-1 font-semibold text-text-secondary"
          style={{ fontSize: "var(--font-micro)" }}
        >
          Example payload
        </div>
        <pre
          className="overflow-x-auto rounded-md border border-border-subtle bg-page p-3 font-mono text-text-primary"
          style={{ fontSize: "var(--font-micro)" }}
        >
          {formatPayload(topic.payload_example)}
        </pre>
      </div>
    </div>
  );
}
